<script setup>
import { computed } from 'vue';

const props = defineProps({
  task: {
    type: Object,
    required: true
  },
  showDate: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['select', 'dragstart', 'dragover', 'drop']);

const isCompleted = computed(() => String(props.task.situation) === '3');

const attachmentsCount = computed(() => {
  if (Array.isArray(props.task.attachments)) return props.task.attachments.length;
  if (typeof props.task.attachments_count === 'number') return props.task.attachments_count;
  return 0;
});

const formatTaskDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '').toUpperCase();
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${weekday} ${day}/${month}/${year}`;
};

const formatTimeRangeAndLocation = computed(() => {
  let timeStr = '';
  if (props.task.hour) {
    timeStr = props.task.hour.substring(0, 5);
  }
  if (props.task.end_hour) {
    timeStr += `-${props.task.end_hour.substring(0, 5)}`;
  }
  if (props.task.location) {
    timeStr = timeStr ? `${timeStr}, ${props.task.location}` : props.task.location;
  }
  return timeStr;
});
</script>

<template>
  <div
    class="kanban-card mb-2"
    @click="emit('select', task)"
    @dragstart="emit('dragstart', task)"
    @dragover.prevent="emit('dragover', task)"
    @drop="emit('drop', task)"
    draggable="true"
  >
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="d-flex align-items-center gap-2">
        <i v-if="!isCompleted" class="fa-regular fa-circle-check text-muted" style="font-size: 14px;"></i>
        <i v-else class="fa-solid fa-circle-check text-success" style="font-size: 14px;"></i>

        <Tag v-if="String(task.situation) === '1'" rounded style="background-color: #E9ECEF; color: #6C757D; padding: 2px 8px;">
          <span class="tag-text">À iniciar</span>
        </Tag>
        <Tag v-if="String(task.situation) === '2'" rounded style="background-color: #CFF4FC; color: #087990; padding: 2px 8px;">
          <span class="tag-text">Em andamento</span>
        </Tag>
        <Tag v-if="String(task.situation) === '4'" rounded style="background-color: #F8D7DA; color: #842029; padding: 2px 8px;">
          <span class="tag-text">Bloqueada</span>
        </Tag>
        <Tag v-if="isCompleted" rounded style="background-color: rgba(9,167, 6, 0.1); color: rgb(9 167 6); padding: 2px 8px;">
          <span class="tag-text">Concluída</span>
        </Tag>
      </div>

      <div class="priority-stack">
        <div v-if="String(task.priority) === '1'" class="d-flex align-items-center">
          <i class="fa-solid fa-angle-up" style="color: #0D6EFD; font-size: 12px;"></i>
        </div>
        <div v-if="String(task.priority) === '2'" class="d-flex flex-column align-items-center justify-content-center" style="color: #FFC107; line-height: 0.2;">
          <i class="fa-solid fa-angle-up" style="font-size: 10px; margin-bottom: -6px;"></i>
          <i class="fa-solid fa-angle-up" style="font-size: 10px;"></i>
        </div>
        <div v-if="String(task.priority) === '3'" class="d-flex flex-column align-items-center justify-content-center" style="color: #F76659; line-height: 0.2;">
          <i class="fa-solid fa-angle-up" style="font-size: 10px; margin-bottom: -6px;"></i>
          <i class="fa-solid fa-angle-up" style="font-size: 10px; margin-bottom: -6px;"></i>
          <i class="fa-solid fa-angle-up" style="font-size: 10px;"></i>
        </div>
      </div>
    </div>

    <div class="task-title mb-1" :title="task.title">
      <span v-if="!isCompleted">{{ task.title }}</span>
      <s v-else class="text-muted">{{ task.title }}</s>
    </div>

    <div v-if="formatTimeRangeAndLocation" class="text-muted mb-2 task-subtext" :title="formatTimeRangeAndLocation">
      {{ formatTimeRangeAndLocation }}
    </div>

    <div v-if="showDate && task.date" class="text-muted mb-2" style="font-size: 11px; font-weight: 600; text-transform: uppercase;">
      {{ formatTaskDate(task.date) }}
    </div>

    <div class="d-flex justify-content-between align-items-center mt-2">
      <div class="d-flex align-items-center gap-3 text-muted" style="font-size: 12px;">
        <div v-if="attachmentsCount > 0" class="d-flex align-items-center gap-1">
          <i class="fa-solid fa-paperclip" style="color: #ADB5BD;"></i>
          <span style="font-size: 11px; font-weight: 600; color: #6C757D;">{{ attachmentsCount }}</span>
        </div>
      </div>

      <div class="d-flex align-items-center" v-if="task.users && task.users.length > 0">
        <img
          v-for="(user, uIdx) in task.users.slice(0, 2)"
          :key="user.id || uIdx"
          :src="$userAvatar(user.path_image)"
          :alt="user.name"
          v-tooltip.top="user.name"
          class="avatar-chip"
          :style="{ marginLeft: uIdx > 0 ? '-6px' : '0' }"
        />
        <div
          v-if="task.users.length > 2"
          class="avatar-chip-more d-flex align-items-center justify-content-center"
          style="margin-left: -6px;"
        >
          +{{ task.users.length - 2 }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card {
  background-color: #FFFFFF;
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-width: 0;
  overflow: hidden;
}

.kanban-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.task-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: #212529;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.task-subtext {
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag-text {
  font-size: 10px;
  font-weight: 700;
}

.avatar-chip {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid #ffffff;
}

.avatar-chip-more {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: rgba(241, 134, 11, 0.15);
  color: #F1860B;
  font-size: 9px;
  font-weight: 700;
  border: 1.5px solid #ffffff;
}
</style>